import { useEffect } from 'react';

/**
 * Disables pinch-zoom and double-tap-zoom gestures app-wide.
 *
 * `index.html`'s viewport meta (`user-scalable=no`) plus `touch-action: pan-x pan-y`
 * on html/body cover Android Chrome, but iOS Safari deliberately ignores both, so
 * a JS-level guard is required there too:
 *  - `gesturestart`/`gesturechange`/`gestureend` are WebKit-only pinch events.
 *  - `touchmove` is only blocked when a SECOND touch point is present (genuine
 *    pinch), never for single-finger touchmove — that would break scrolling and
 *    every single-finger swipe gesture in the app.
 *
 * Listeners must be registered with `{ passive: false }`, otherwise
 * `preventDefault()` is silently ignored by the browser.
 */
export function useDisablePinchZoom(): void {
  useEffect(() => {
    const preventGesture = (event: Event) => {
      event.preventDefault();
    };

    const preventMultiTouchMove = (event: TouchEvent) => {
      if (event.touches.length > 1) {
        event.preventDefault();
      }
    };

    document.addEventListener('gesturestart', preventGesture, { passive: false });
    document.addEventListener('gesturechange', preventGesture, { passive: false });
    document.addEventListener('gestureend', preventGesture, { passive: false });
    document.addEventListener('touchmove', preventMultiTouchMove, { passive: false });

    return () => {
      document.removeEventListener('gesturestart', preventGesture);
      document.removeEventListener('gesturechange', preventGesture);
      document.removeEventListener('gestureend', preventGesture);
      document.removeEventListener('touchmove', preventMultiTouchMove);
    };
  }, []);
}
